import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {TranslatePipe} from '@brunata-angular/util';
import {
    CheckboxAreaDirective,
    CheckboxGroupComponent,
    ControlMessagesComponent,
    patchControlMethod,
} from '@brunata-angular/ui';
import {
    ExportFileFormat,
    ExportFileFormatForm,
    MAX_DECIMAL_PRECISION,
} from '../../shared/models/export-file-format.model';
import {SettingsRowComponent} from './settings-row.component';

@Component({
    selector: 'app-numbers-tab',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        CheckboxAreaDirective,
        CheckboxGroupComponent,
        ControlMessagesComponent,
        SettingsRowComponent,
    ],
    template: `
        <app-settings-row [hintKey]="decimalPrecisionHintKey()">
            <div class="form-group row align-items-center">
                <label class="col-form-label col-5" [for]="precisionInputId">
                    {{
                        'customizedexports_export_fileformat_decimal_precision_label'
                            | translate: 'online' : 'export'
                    }}
                </label>
                <div class="col-7">
                    <div class="d-flex align-items-center flex-wrap column-gap-3 row-gap-2">
                        <input
                            type="number"
                            class="form-control precision-input"
                            step="1"
                            min="0"
                            [id]="precisionInputId"
                            [max]="maxDecimalPrecision"
                            [formControl]="form().controls.decimalPrecision" />
                        <app-checkbox-group
                            appCheckboxArea
                            class="flex-grow-1 align-self-stretch d-flex align-items-center"
                            [formControl]="form().controls.exportDecimalsAsStored"
                            [label]="
                                'customizedexports_export_fileformat_decimals_as_stored_label'
                                    | translate: 'online' : 'export'
                            " />
                    </div>
                    <app-control-messages [control]="form().controls.decimalPrecision" />
                </div>
            </div>
        </app-settings-row>
    `,
    styles: `
        .form-group {
            margin-bottom: 0;
        }

        .precision-input {
            width: 5rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumbersTabComponent implements OnInit, OnDestroy {
    readonly form = input.required<ExportFileFormatForm>();
    readonly settings = input.required<ExportFileFormat>();

    protected readonly maxDecimalPrecision = MAX_DECIMAL_PRECISION;
    protected readonly precisionInputId = 'customized-export-decimal-precision';

    protected readonly decimalPrecisionHintKey = computed(() =>
        this.settings().decimalPrecision === null
            ? 'customizedexports_export_fileformat_decimals_as_stored_hint'
            : 'customizedexports_export_fileformat_decimals_fixed_hint',
    );

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private restorePatch: (() => void) | undefined;

    /** OnPush never notices markAsTouched, so it would hide this error otherwise. (AGI-19494) */
    ngOnInit(): void {
        const control = this.form().controls.decimalPrecision;
        const markForCheck = (): void => this.changeDetectorRef.markForCheck();
        const restoreTouched = patchControlMethod(control, 'markAsTouched', markForCheck);
        const restoreUntouched = patchControlMethod(control, 'markAsUntouched', markForCheck);
        this.restorePatch = (): void => {
            restoreTouched();
            restoreUntouched();
        };
    }

    ngOnDestroy(): void {
        this.restorePatch?.();
    }
}
